import { SECURITY_CONFIG } from '../config/security';

interface RateLimitEntry {
  count: number;
  resetTime: number;
  blockedUntil?: number;
}

class RateLimiter {
  private storage: Map<string, RateLimitEntry> = new Map();
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.startCleanup();
  }

  private startCleanup() {
    this.cleanupInterval = setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of this.storage.entries()) {
        if (entry.resetTime < now && (!entry.blockedUntil || entry.blockedUntil < now)) {
          this.storage.delete(key);
        }
      }
    }, 60000);
  }

  private getKey(identifier: string, type: 'auth' | 'api'): string {
    return `${type}:${identifier}`;
  }

  checkLimit(identifier: string, type: 'auth' | 'api' = 'api'): {
    allowed: boolean;
    remainingAttempts?: number;
    resetTime?: number;
    blockedUntil?: number;
  } {
    const key = this.getKey(identifier, type);
    const now = Date.now();
    const entry = this.storage.get(key);
    const config = type === 'auth' ? SECURITY_CONFIG.rateLimit.auth : SECURITY_CONFIG.rateLimit.api;

    if (!entry || entry.resetTime < now) {
      const newEntry: RateLimitEntry = {
        count: 1,
        resetTime: now + config.windowMs
      };
      this.storage.set(key, newEntry);
      return {
        allowed: true,
        remainingAttempts: config.maxAttempts - 1,
        resetTime: newEntry.resetTime
      };
    }

    if (entry.blockedUntil && entry.blockedUntil > now) {
      return {
        allowed: false,
        blockedUntil: entry.blockedUntil
      };
    }

    if (entry.blockedUntil && entry.blockedUntil <= now) {
      const newEntry: RateLimitEntry = {
        count: 1,
        resetTime: now + config.windowMs
      };
      this.storage.set(key, newEntry);
      return {
        allowed: true,
        remainingAttempts: config.maxAttempts - 1,
        resetTime: newEntry.resetTime
      };
    }

    const maxAttempts = type === 'auth' ? config.maxAttempts : config.maxRequests;

    if (entry.count >= maxAttempts) {
      const blockDuration = type === 'auth' ? config.blockDurationMs : config.windowMs;
      entry.blockedUntil = now + blockDuration;
      this.storage.set(key, entry);
      return {
        allowed: false,
        blockedUntil: entry.blockedUntil
      };
    }

    entry.count++;
    this.storage.set(key, entry);

    return {
      allowed: true,
      remainingAttempts: maxAttempts - entry.count,
      resetTime: entry.resetTime
    };
  }

  reset(identifier: string, type: 'auth' | 'api' = 'api'): void {
    const key = this.getKey(identifier, type);
    this.storage.delete(key);
  }

  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.storage.clear();
  }
}

export const rateLimiter = new RateLimiter();

export const checkAuthRateLimit = (identifier: string) => {
  return rateLimiter.checkLimit(identifier, 'auth');
};

export const checkApiRateLimit = (identifier: string) => {
  return rateLimiter.checkLimit(identifier, 'api');
};

export const resetAuthRateLimit = (identifier: string) => {
  rateLimiter.reset(identifier, 'auth');
};
